import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Box,
  Text,
  useToast
} from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileService } from "../../utils/FileService";
import FileList from "../../components/FileList";
import { ProjectContext } from '../InvestmentScreening/InvestmentScreeningDetail';

/**
 * 拡張子配列からaccept属性の値を生成する
 * @param extensions 許可する拡張子の配列（例: ["jpg", "jpeg", "png"]）
 * @returns accept属性の値（例: ".jpg,.jpeg,.png"）
 */
const getAcceptAttribute = (extensions: string[] | undefined): string | undefined => {
  if (!extensions || extensions.length === 0) {
    return undefined;
  }
  return extensions.map(ext => `.${ext}`).join(',');
};

/**
 * ファイルリスト
 * @param files ファイル情報
 * @param onDelete ファイル削除関数
 * @returns ファイルリスト
 */
const FileUploadModal: React.FC<any> = ({
  isOpen,
  onClose,
  title,
  projectId,
  folderName,
  allowedExtensions,
  onSaveComplete,
}) => {

  const { t } = useTranslation();
  /** トースト表示用 */
  const toast = useToast();

  /** 振り返りモードの reviewDate（ProjectContext 経由） */
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? undefined;

  /** ファイル操作用 */
  const {
    uploadedFiles,
    fetchFiles,
    handleFileSelect,
    uploadFiles,
    handleDelete
  } = useFileService(title, `${projectId}/${folderName}`, reviewDate);

  // 保存状態
  const [isSaving, setIsSaving] = useState(false);

  // 変更状態
  const [isEdited, setIsEdited] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // accept属性の値を生成
  const acceptAttribute = useMemo(() => getAcceptAttribute(allowedExtensions), [allowedExtensions]);

  useEffect(() => {
    if (!isOpen) return;
    setIsEdited(false);

    // アップロード済みファイル情報取得
    (async () => {
      try {
        await fetchFiles();
      } catch (err) {
        console.error('listFiles failed', err);
      }
    })();
  }, [isOpen]);

  /**
   * 確認モーダルで保存ボタン押下
   */
  const handleConfirm = async () => {

    // 保存状態を保存中に更新
    setIsSaving(true);

    try {
      await uploadFiles();

      // コールバックがあれば実行
      if (onSaveComplete) {
        await onSaveComplete();
      }

      toast({
        title,
        description: t('components.FileUploadModal.saveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (err) {
      console.error('uploadFiles failed', err);
      toast({
        title,
        description: t('components.FileUploadModal.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      // 状態を初期化
      setIsSaving(false);
    }
  }

  /**
   * 削除アイコン押下処理
   */
  const onDelete = (file: any) => {
    handleDelete(file);
    setIsEdited(true);
  };

  return (
    < Modal isOpen={isOpen} onClose={onClose} size="xl" >
      <ModalOverlay />
      <ModalContent height="680px">
        <ModalHeader>{t('components.FileUploadModal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={2} color="red">{t('components.FileUploadModal.personalInfoNote')}</Text>
          <Box>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
              accept={acceptAttribute}
              onChange={(e) => {
                const addedCount = handleFileSelect(e, allowedExtensions);
                if (addedCount > 0) {
                  setIsEdited(true);
                }
              }}
            />
            <Button onClick={() => fileInputRef.current?.click()}>
              {t('components.FileUploadModal.fileSelect')}
            </Button>
          </Box>
          <FileList files={uploadedFiles ?? []} onDelete={onDelete} />
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} isDisabled={isSaving || !isEdited} isLoading={isSaving} onClick={handleConfirm}>
            {t('components.FileUploadModal.save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('components.FileUploadModal.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal >
  );
}

export default FileUploadModal;
